import React from 'react'
import { Text, Picture } from '../../../components/index.js'
import { cn } from '../../../helpers/index.js'
import type { CompanyStatsBannerProps, StatItem } from './types.js'

/**
 * 统计项组件
 */
const StatItemComp = ({ stat, theme, isLast }: { stat: StatItem; theme?: 'light' | 'dark'; isLast?: boolean }) => {
  return (
    <div
      className={cn(
        "tablet:after:hidden relative flex flex-col gap-1 after:absolute after:bottom-1 after:right-0 after:h-[calc(100%-8px)] after:w-px after:bg-[#3A3C40] after:content-['']",
        { 'after:hidden': isLast }
      )}
    >
      <Text
        as="div"
        html={stat.value}
        className={cn('desktop:text-[32px] tablet:text-[24px] text-[14px] leading-[1.2]', {
          'text-white': theme !== 'dark',
          'text-black': theme === 'dark',
        })}
      />
      <Text
        as="p"
        html={stat.label}
        className={cn('desktop:text-base text-balance text-sm font-bold leading-[1.4] tracking-[-0.28px]', {
          'text-white': theme !== 'dark',
          'text-black': theme === 'dark',
        })}
      />
    </div>
  )
}

/**
 * 公司统计横幅组件
 * 用于展示公司 Logo、标语和关键统计数据
 */
const CompanyStatsBanner = React.forwardRef<HTMLDivElement, CompanyStatsBannerProps>(({ data, className }, ref) => {
  const { logo, tagline, stats = [], theme = 'dark' } = data

  return (
    <div
      ref={ref}
      data-ui-component-id="CompanyStatsBanner"
      className={cn(
        'desktop:h-[200px] lg-desktop:h-[240px] relative flex w-full items-center justify-between overflow-hidden',
        'p-4',
        'tablet:p-6',
        'desktop:px-16 desktop:py-8',
        'lg-desktop:px-32',
        {
          'bg-black': theme !== 'dark',
          'bg-white': theme === 'dark',
        },
        className
      )}
    >
      {/* 内容区域 */}
      <div
        className={cn(
          'relative z-10 flex w-full flex-col gap-4',
          'desktop:flex-row desktop:items-center desktop:justify-between'
        )}
      >
        {/* 左侧: Logo + 分隔线 + 标语 */}
        <div className="tablet:flex-row tablet:gap-4 flex shrink-0 flex-col items-start gap-2">
          {logo?.url && (
            <Picture
              source={logo.url}
              alt={logo.alt || 'Company Logo'}
              className="tablet:h-10 h-9 w-auto"
              imgClassName="h-full w-auto object-contain"
            />
          )}
          {logo?.url && tagline && (
            <div
              className={cn('tablet:block tablet:h-10 hidden h-8 w-px', {
                'bg-white': theme !== 'dark',
                'bg-black': theme === 'dark',
              })}
            />
          )}
          {tagline && (
            <Text
              as="p"
              html={tagline}
              className={cn('text-sm font-bold leading-[1.4]', 'tablet:max-w-[320px] tablet:text-base', {
                'text-white': theme !== 'dark',
                'text-black': theme === 'dark',
              })}
            />
          )}
        </div>

        {/* 右侧: 统计数据 */}
        {stats.length > 0 && (
          <div
            className={cn('tablet:flex grid grid-cols-3 gap-4', 'tablet:flex-nowrap tablet:gap-10 desktop:gap-[50px]')}
          >
            {stats.map((stat, index) => (
              <div key={`stat-${index}`}>
                <StatItemComp stat={stat} theme={theme} isLast={index === stats.length - 1} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
})

CompanyStatsBanner.displayName = 'CompanyStatsBanner'

export default CompanyStatsBanner
export type { CompanyStatsBannerProps, StatItem }
